// Prequalification funnel — the RESULT route (§7). One page per role
// (/portal/prequalify/{buyer|seller}/result) reading the finalized state
// the funnel persisted to sessionStorage. Two views: the ceremony (seal +
// hero + reference strip + a single NEXT button) and the documents view
// (live document cards, the buyer Model-a-deal card, the emailed-to-you
// confirmation, and a quiet "Adjust my answers" link back into the
// funnel). A SoftDecline finalize renders the warm thank-you instead; a
// direct visit with no stored finalized state redirects into the funnel.
//
// Both of those funnel-facing behaviours are conditional on there BEING a
// funnel behind this page — see `PrequalResultPageProps.homeHref`, which the
// staff assisted namespace sets because its form is addressed by draft id.
//
// ── WHOSE SITE THIS SCREEN BELONGS TO ────────────────────────────────────
//
// The FINALIZED RESULT says, and nothing else is asked.
//
// The URL cannot say. An assisted application is addressed by draft id and
// navigates here to a route with no `:slug` at all, which is why the last two
// screens of a branded assisted application used to revert to Phoenix — the
// broker's client watching their own documents appear under the wrong company.
//
// And the URL is not authoritative even when it does carry a slug: a live
// invite BEATS the requested slug for attribution server-side, so a submission
// can be credited to brand X while the address bar said brand Y. Reading the
// slug the server RECORDED is what makes the skin on this screen incapable of
// disagreeing with the brand the submission actually belongs to.
//
// It also survives a reload, which the address bar cannot do here: the blob is
// in sessionStorage, which a refresh preserves, so the brand is re-resolved
// from the same recorded value rather than lost.

import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { cn } from "@/design-system/lib/utils";
import { PortalShell } from "../../components/PortalShell";
import type { PrequalFinalizeResult, PrequalRole } from "../../api/prequalification";
import {
  DocumentCeremonyCards,
  ReferenceStrip,
  ResultHero,
} from "./PrequalBits";
import { useFunnelCopy, usePartnerBrand, usePrequalBase } from "./usePartnerBrand";
import { PrequalBrandProvider } from "./PrequalBrandContext";
import { PartnerFallbackNotice, PartnerThemeProvider } from "./PartnerThemeProvider";
import { hasText } from "./blankCopy";
import {
  loadFunnelState,
  PREQUAL_BUYER_STORAGE_KEY,
  PREQUAL_SELLER_STORAGE_KEY,
} from "./prequalStorage";
import { useFreshDocumentLinks, useProgramBrochureUrl } from "./usePrequalResultDocuments";

const CARD_SHELL =
  "rounded-[14px] border border-line bg-white-pure shadow-[0_10px_34px_rgba(20,20,20,0.07)]";

export interface PrequalResultPageProps {
  role: PrequalRole;
  /**
   * Where to send someone when the FUNNEL is not behind this page.
   *
   * The public funnel mounts the buyer/seller forms alongside this route, so
   * both the no-state redirect and "Adjust my answers" name `{base}/{role}` and
   * they work. Pass nothing and every one of those behaviours is unchanged.
   *
   * The STAFF assisted namespace has no such route — its funnel is addressed by
   * draft id (`/deal-flow/prequalify/assist/{draftId}`), which this screen has
   * no way to reconstruct. So a host that sets this is telling the page two
   * things at once, and both matter:
   * <ul>
   *   <li>the redirect gets a destination that EXISTS, instead of bouncing a
   *       direct visit into a 404;</li>
   *   <li>"Adjust my answers" is HIDDEN, because there is nowhere to adjust
   *       them. Re-pointing it at the host's index page would leave a control
   *       that does something other than what it says — worse than its absence,
   *       and the one thing the shared Table &amp; Tile standard is explicit
   *       about.</li>
   * </ul>
   */
  homeHref?: string;
}

/**
 * Reads the finalized application and names the site it was attributed to.
 *
 * Split from the body because a component cannot consume a provider it renders
 * itself, and the body is what reads the brand.
 *
 * `partnerSlug` is a TRI-STATE and every arm is meaningful:
 * <ul>
 *   <li><b>a string</b> — the recorded site; resolve it.</li>
 *   <li><b>`null`</b> — a Phoenix-run application; render native, fetch
 *       nothing.</li>
 *   <li><b>`undefined`</b> — the field is absent, which is a blob written
 *       before this existed (a session open across the deploy). Defer to the
 *       route, which is exactly what this screen did before, so an in-flight
 *       application degrades to the old behaviour instead of to a wrong one.</li>
 * </ul>
 */
export function PrequalResultPage({ role, homeHref }: PrequalResultPageProps) {
  const buyer = role === "Buyer";
  const storageKey = buyer ? PREQUAL_BUYER_STORAGE_KEY : PREQUAL_SELLER_STORAGE_KEY;
  const base = usePrequalBase();
  const funnelPath = buyer ? `${base}/buyer` : `${base}/seller`;

  // Read once at mount — the funnel wrote the state synchronously before
  // navigating here; the shape guard drops stale/invalid blobs.
  const [stored] = useState(() => loadFunnelState<{ email?: string }>(storageKey));
  const finalized = stored?.finalized ?? null;

  if (!finalized) {
    // Direct visit with no finalized state — back into the funnel, or to
    // whatever the host offered instead when it has no funnel.
    return <Navigate to={homeHref ?? funnelPath} replace />;
  }

  return (
    <PrequalBrandProvider source={finalized.partnerSlug}>
      <PrequalResultBody
        role={role}
        finalized={finalized}
        email={typeof stored?.form.email === "string" ? stored.form.email : ""}
        funnelPath={funnelPath}
        // `undefined` (no host override) is what keeps the link; a host that
        // named a home has no funnel to send anyone back into.
        canAdjust={homeHref === undefined}
        base={base}
      />
    </PrequalBrandProvider>
  );
}

function PrequalResultBody({
  role,
  finalized,
  email,
  funnelPath,
  canAdjust,
  base,
}: {
  role: PrequalRole;
  finalized: PrequalFinalizeResult;
  email: string;
  funnelPath: string;
  /** False when the host has no funnel behind this screen — see
   *  {@link PrequalResultPageProps.homeHref}. */
  canAdjust: boolean;
  base: string;
}) {
  const buyer = role === "Buyer";
  const { brand, loading: brandPending, notFound } = usePartnerBrand();
  // Server-published fine print, falling back to the compiled-in constant.
  const { indicativeLabel } = useFunnelCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const result = finalized.result;

  // 🔴 FRESH download links, asked for on MOUNT rather than replayed out of
  // sessionStorage. A finalize link is a 15-minute blob SAS and this screen is
  // rendered from a stored blob on every visit and every reload, so before this
  // every download button here was dead a quarter of an hour after the person
  // finished — with the page still offering them and nothing to explain it. Null
  // (no token, an expired one, a failed read) falls back to the stored links,
  // which is this screen's previous behaviour rather than a new failure.
  //
  // Called unconditionally, above the SoftDecline early return below, because
  // these are hooks. Neither fires a request on that path: a decline carries no
  // result token, and the brochure read is gated off explicitly — a decline has no
  // documents view to put a card in, so asking would be noise on an anonymous
  // rate-limited endpoint.
  //
  // Both DO fire on the ceremony view, deliberately: they are side-effect-free, and
  // resolving here means the documents view has its links in hand the instant the
  // reader presses NEXT.
  const passed = result.outcome !== "SoftDecline";
  // 🔴 The token is withheld on a decline DELIBERATELY, and it stopped being
  // automatic. A decline used to carry no ResultToken at all, so this hook was
  // inert for one by construction; the token is minted for every outcome now, and
  // passing it unguarded would fire a documents read on an anonymous,
  // rate-limited endpoint whose answer this screen has nowhere to render. Gated
  // the same way the brochure below it already is.
  const freshDocuments = useFreshDocumentLinks(passed ? finalized.resultToken : null);
  const brochureUrl = useProgramBrochureUrl(role, passed);

  // SoftDecline — the warm thank-you. No ceremony, and no documents VIEW.
  //
  // 🔴 The sentence here used to read "No documents are issued until that
  // conversation happens." That became false the day a decline got a letter and
  // an analysis of its own: the applicant would have read it on screen and then
  // found both attached to an email, which is Phoenix contradicting itself on the
  // one surface a declined person is most likely to re-read. It now says what
  // actually happens.
  //
  // What it still does NOT do is offer the downloads. The credential exists —
  // ResultToken is minted for every outcome now — and the documents view is built
  // around a congratulations ceremony a decline must not enter, so putting a
  // download block here is a new customer-facing surface on a screen whose copy is
  // pending counsel, rather than a correction. Left for that decision.
  if (result.outcome === "SoftDecline") {
    return (
      <PartnerThemeProvider brand={brand}>
      <PortalShell pageTitle="Prequalification" publicFunnel partner={brand} brandPending={brandPending}>
        {notFound && <PartnerFallbackNotice />}
        <div
          className={cn(CARD_SHELL, "p-space-4 sm:p-space-6 max-w-2xl mx-auto")}
          data-testid="prequal-finalized-softdecline"
        >
          <h1 className="font-semibold normal-case tracking-normal text-[1.45rem] text-ink mt-0 mb-space-2">
            Thanks — we’ll reach out
          </h1>
          <p className="text-small text-ink-mid m-0">
            Your information is saved. One or more of your answers needs a
            conversation, and someone from Phoenix Capital will contact you at{" "}
            <strong className="text-ink">{email}</strong> to talk it through.
            We have emailed that address a letter explaining the review, an
            analysis of what would need to change, and an overview of the
            program.
          </p>
        </div>
      </PortalShell>
      </PartnerThemeProvider>
    );
  }

  const view = searchParams.get("view") === "documents" ? "documents" : "ceremony";

  return (
    <PartnerThemeProvider brand={brand}>
    <PortalShell pageTitle={buyer ? "Buyer prequalification" : "Seller prequalification"} wide publicFunnel partner={brand} brandPending={brandPending}>
      {notFound && <PartnerFallbackNotice />}
      <div className="max-w-2xl mx-auto mt-space-4 flex flex-col gap-space-4">
        {view === "ceremony" ? (
          <>
            <ResultHero
              role={role}
              result={result}
              internalPurchase={finalized.buyerDocumentPath === "InternalBuyer"}
            />
            <ReferenceStrip
              referenceNumber={finalized.referenceNumber}
              validThroughUtc={finalized.validThroughUtc}
            />
            <div className="text-center">
              <button
                type="button"
                onClick={() => setSearchParams({ view: "documents" })}
                data-testid="prequal-result-next"
                className="rounded-pill bg-ink-black text-gold border border-ink-black px-space-3 sm:px-space-5 py-space-2 min-h-space-6 sm:min-h-0 text-label uppercase tracking-wider sm:tracking-[0.18em] cursor-pointer transition-colors duration-fast ease-standard hover:bg-gold-deep hover:border-gold-deep hover:text-white-pure focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                NEXT — YOUR DOCUMENTS →
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-space-4" data-testid="prequal-result-documents">
            <h1 className="font-semibold normal-case tracking-normal text-[1.45rem] text-ink m-0">
              Your documents
            </h1>
            <ReferenceStrip
              referenceNumber={finalized.referenceNumber}
              validThroughUtc={finalized.validThroughUtc}
            />
            <DocumentCeremonyCards
              role={role}
              finalized={finalized}
              base={base}
              freshDocuments={freshDocuments}
              brochureUrl={brochureUrl}
            />
            {finalized.emailSent === true && (
              <p className="text-small text-ink-mid m-0" data-testid="prequal-emailed-line">
                We’ve also emailed your documents to {email}.
              </p>
            )}
            {canAdjust && (
              <p className="text-small m-0">
                <Link
                  to={funnelPath}
                  data-testid="prequal-adjust"
                  className="text-ink-mid underline hover:text-gold-deep"
                >
                  Adjust my answers
                </Link>
              </p>
            )}
            {hasText(indicativeLabel) && (
              <p className="text-caption text-ink-faint m-0" data-testid="prequal-indicative-label">{indicativeLabel}</p>
            )}
          </div>
        )}
      </div>
    </PortalShell>
    </PartnerThemeProvider>
  );
}
